/* eslint-disable @typescript-eslint/no-explicit-any */
import { head, inRange, last } from 'lodash'
import { PauseCircle, XCircle } from 'lucide-react'
import React from 'react'
import { Badge, Column, Row, Show } from '@/components/core'
import { classNameBuilder } from '@/helpers'
import translations from '@/translations'

type UserMessageTranscriptionType =
  | { type: 'long-pause' }
  | { type: 'message' | 'wrong' | 'correct'; message: string }

export interface UserMessageTranscriptProps {
  wordsList?: any[]
  grammarErrors?: any[]
  longPauses?: any[]
  className?: string
}

export const UserMessageTranscript: React.FC<UserMessageTranscriptProps> = ({
  wordsList,
  grammarErrors = [],
  longPauses = [],
  className
}) => {
  const transcriptionList = React.useMemo<
    UserMessageTranscriptionType[]
  >(() => {
    if (wordsList) {
      const sourceWordList = wordsList.reduce((acc: any[], wordItem) => {
        if (wordItem.source_word && wordItem.source_word_continue) {
          last(acc).word = wordItem.source_word
          last(acc).ending_punctuation = wordItem.ending_punctuation
          return acc
        }
        return [...acc, wordItem]
      }, [] as any[])

      const transcript =
        (sourceWordList.reduce((acc, wordScore, mainIndex) => {
          const markups: any[] = []

          const currentWordLength =
            wordScore.word.length +
            Number(wordScore.ending_punctuation?.length || 0)

          const accumulatedTextLength = sourceWordList
            .filter((_, index) => index < mainIndex)
            .map((w) => `${w.word}${w.ending_punctuation || ''}`)
            .join(' ').length

          const grammarError = grammarErrors.find((e) =>
            inRange(accumulatedTextLength + 1, e.startIndex, e.endIndex)
          )

          if (grammarError) {
            if (grammarError.startIndex === accumulatedTextLength + 1) {
              markups.push({
                message: grammarError.originalText,
                type: 'wrong'
              })
            }
            if (
              grammarError.endIndex ===
              accumulatedTextLength + currentWordLength + 1
            ) {
              markups.push({
                message: grammarError.suggestion,
                type: 'correct'
              })
            }
          } else {
            markups.push({
              message: `${wordScore.word}${wordScore.ending_punctuation || ''}`,
              type: 'message'
            })
          }

          const lastExtent = last(
            (last(wordScore?.syllable_score_list) as any)?.extent
          )
          if (longPauses.some((pause) => head(pause) === lastExtent)) {
            markups.push({ type: 'long-pause' })
          }

          return [...acc, ...markups]
        }, []) as any[]) || []

      return transcript
    }

    return []
  }, [wordsList, grammarErrors, longPauses])

  return (
    <Column className={classNameBuilder('gap-y-2 bg-transparent', className)}>
      <Row className="w-full flex-wrap items-center justify-end space-x-1 border-r-2 border-electric-indigo-200 px-2 py-2">
        {transcriptionList.map((message, index) => {
          return <MapTranscript key={index} message={message} />
        })}
      </Row>
      <Row className="justify-end gap-2">
        <Show when={!!longPauses?.length}>
          <Badge color="yellow" className="gap-x-2">
            <PauseCircle size={16} />
            {longPauses.length}{' '}
            {longPauses.length === 1
              ? translations.long_pause_label_conversation_analysis
              : translations.long_pauses_plural_label_conversation_analysis}
          </Badge>
        </Show>
        <Show when={!!grammarErrors.length}>
          <Badge color="red" className="gap-x-2">
            <XCircle size={16} />
            {grammarErrors.length}{' '}
            {grammarErrors.length === 1
              ? translations.error_label_conversation_analysis
              : translations.errors_plural_label_conversation_analysis}
          </Badge>
        </Show>
      </Row>
    </Column>
  )
}

export default UserMessageTranscript

const MapTranscript: React.FC<{ message: UserMessageTranscriptionType }> = ({
  message
}) => {
  return message.type == 'long-pause' ? (
    <PauseCircle size={16} color="#F57F17" />
  ) : message.type == 'message' ? (
    <span className="text-[1rem] text-electric-indigo-900/80">
      {message.message}
    </span>
  ) : message.type == 'correct' ? (
    <Badge color="blue" size="16">
      {message.message}
    </Badge>
  ) : message.type == 'wrong' ? (
    <span className="text-sm text-[#F90303] line-through">
      {message.message}
    </span>
  ) : null
}
